<i18n>
{
  "fr": {
    "procedures": "Procédure",
    "procedures-not-set": "La procédure n'est pas définie pour cet indicateur.",
    "procedure-clear": "Réinitialiser la procédure",
    "procedure-clear-confirm-title": "Réinitialiser la procédure",
    "procedure-clear-confirm-message": "Êtes-vous certain de vouloir réinitialiser la procédure? Le contenu personnalisé sera supprimé et le texte par défaut pour cet indicateur sera utilisé.",
    "procedure-clear-cancel": "Annuler",
    "procedure-clear-confirm": "Réinitialiser",
    "procedure-unofficial": "Non-officialisée",
    "procedure-edit-formalize": "Éditer et officialiser",
    "procedure-edit": "Éditer",
    "procedure-save": "Officialiser et sauvegarder",
    "navigate-to-indicator-template": "Éditer dans le gabarit de projet"
  },
  "en": {
    "procedures": "Procedure",
    "procedures-not-set": "The procedure is not defined for this indicator.",
    "procedure-clear": "Reset procedure",
    "procedure-clear-confirm-title": "Reset procedure",
    "procedure-clear-confirm-message": "Are you sure that you want to reset the procedure? The customized content will be deleted and the default text for this indicator will be used.",
    "procedure-clear-cancel": "Cancel",
    "procedure-clear-confirm": "Reset",
    "procedure-unofficial": "Unofficial",
    "procedure-edit-formalize": "Edit and formalize",
    "procedure-edit": "Edit",
    "procedure-save": "Formalize and save",
    "navigate-to-indicator-template": "Edit in the project template"
  }
}
</i18n>
<template>
  <prismic-content class="has-max-width-768" :doctype="DOCUMENT_TYPE_INDICATOR" :uid="indicator.prismicUid" v-slot="{ content }">
    <prismic-content
      :doctype="$consts.prismic.DOCUMENT_TYPE_INDICATOR_DETAIL"
      v-slot="{ content: detailContent }"
      class="indicator"
      @loaded="$emit('ready')"
    >
      <h2 class="title is-4 has-text-weight-bold mt-0">
        {{ $t('procedures') }}
        <b-tag type="is-danger" v-if="!indicator.officialProcedure">
          {{ $t('procedure-unofficial') }}
        </b-tag>
      </h2>
      <b-message
        v-if="detailContent && !indicator.officialProcedure && $prismicText(detailContent.unofficial_procedure)"
        type="is-danger"
        class="my-24px is-size-7"
      >
        <prismic-rich-text :field="detailContent.unofficial_procedure" />
      </b-message>

      <b-message
        v-if="
          showModifiedProcedureTemplateMessage &&
          detailContent &&
          $prismicText(detailContent.can_not_be_modified_procedure_message)
        "
        type="is-info"
        class="my-24px is-size-7"
      >
        <prismic-rich-text :field="detailContent.can_not_be_modified_procedure_message" />
        <b-button type="is-link" class="mt-2" icon-left="edit" size="is-small" outlined @click="navigateToIndicatorTemplate()">
          {{ $t('navigate-to-indicator-template') }}
        </b-button>
      </b-message>

      <editable-field
        :value="indicator.officialProcedure || $prismicHtml(content.procedures) || ''"
        @save="({ value, closeEditor }) => saveProcedure(value, closeEditor)"
        :edit-label="!indicator.officialProcedure ? $t('procedure-edit-formalize') : $t('procedure-edit')"
        :save-label="$t('procedure-save')"
        :readOnly="readOnly"
      >
        <div class="content is-custom">
          <div v-if="indicator.officialProcedure" v-html="indicator.officialProcedure" />
          <prismic-rich-text v-else-if="$prismicHasText(content.procedures)" :field="content.procedures" />
          <p v-else>{{ $t('procedures-not-set') }}</p>
        </div>

        <template v-slot:editor="editor">
          <rich-text-editor v-model="editor.model" @input="editor.update" class="mb-24px" />
        </template>
        <template v-slot:readModeButtons="{ startEditing, editLabel }">
          <b-button
            key="edit"
            type="is-text has-text-link has-text-decoration-none"
            @click="startEditing"
            outlined
            size="is-small"
            icon-left="edit"
            style="margin-left: -6px"
            :disabled="readOnly"
          >
            {{ editLabel }}
          </b-button>
          <b-button
            v-if="indicator.officialProcedure"
            key="clear"
            type="is-danger"
            @click="clearProcedure"
            outlined
            size="is-small"
            icon-left="undo"
            class="ml-4px"
            :disabled="readOnly"
          >
            {{ $t('procedure-clear') }}
          </b-button>
        </template>
      </editable-field>
    </prismic-content>
  </prismic-content>
</template>

<script>
import { DOCUMENT_TYPE_INDICATOR } from '@/consts/prismic'

import EditableField from '@/components/generic/editable-field'
import RichTextEditor from '@/components/generic/rich-text-editor'
import PrismicContent from '@/components/prismic/prismic-content'

export default {
  components: {
    PrismicContent,
    EditableField,
    RichTextEditor
  },
  props: {
    indicator: Object,
    readOnly: Boolean,
    indicatorTemplate: Object
  },
  data() {
    return {
      DOCUMENT_TYPE_INDICATOR
    }
  },
  computed: {
    showModifiedProcedureTemplateMessage() {
      if (!this.readOnly) return false
      if (!this.indicator.isSpecific) return false
      return true
    }
  },
  methods: {
    saveProcedure(value, closeEditor) {
      this.$emit('onSaveProcedure', value, closeEditor)
    },
    clearProcedure() {
      this.$buefy.dialog.confirm({
        title: this.$t('procedure-clear-confirm-title'),
        message: this.$t('procedure-clear-confirm-message'),
        cancelText: this.$t('procedure-clear-cancel'),
        confirmText: this.$t('procedure-clear-confirm'),
        type: 'is-danger',
        onConfirm: () => this.$emit('onClearProcedure')
      })
    },
    navigateToIndicatorTemplate() {
      this.$router.push({
        name: this.$consts.urls.URL_INDICATOR_PROJECT,
        params: { projectId: this.indicatorTemplate.project.id, id: this.indicatorTemplate.id }
      })
    }
  }
}
</script>